export type DdcStatus = 'completed' | 'timeout' | 'error';

export interface BrowserInformation {
  userAgent?: string;
  acceptHeader?: string;
  language?: string;
  colorDepth?: string;
  screenHeight?: string;
  screenWidth?: string;
  timeDifference?: string;
  javaEnabled?: string;
  javaScriptEnabled?: string;
}

export interface DeviceDataCollection {
  status: DdcStatus;
  waitMs: string;
}

export const DEFAULT_ACCEPT_HEADER = 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8';
export const MAX_DDC_WAIT_MS = 600_000;

const toText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

const toPositiveInteger = (value: unknown): string | undefined => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 1 ? String(Math.trunc(parsed)) : undefined;
};

export const getBrowserInformation = (): BrowserInformation => {
  const fields: BrowserInformation = {
    userAgent: toText(navigator.userAgent),
    acceptHeader: DEFAULT_ACCEPT_HEADER,
    language: toText(navigator.language),
    colorDepth: toPositiveInteger(window.screen?.colorDepth),
    screenHeight: toPositiveInteger(window.screen?.height),
    screenWidth: toPositiveInteger(window.screen?.width),
    timeDifference: String(new Date().getTimezoneOffset() || 0),
    javaEnabled: 'false',
    javaScriptEnabled: 'true',
  };
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value !== undefined)) as BrowserInformation;
};

let setupStartedAt: number | null = null;
let setupCompletedAt: number | null = null;
let setupFailed = false;

export const markDdcStarted = () => {
  setupStartedAt = Date.now();
  setupCompletedAt = null;
  setupFailed = false;
};

export const markDdcCompleted = () => {
  if (setupStartedAt !== null && setupCompletedAt === null) {
    setupCompletedAt = Date.now();
  }
};

export const markDdcFailed = () => {
  setupFailed = true;
};

export const resetDdc = () => {
  setupStartedAt = null;
  setupCompletedAt = null;
  setupFailed = false;
};

const toWaitMs = (elapsed: number) => String(Math.min(Math.max(Math.trunc(elapsed), 0), MAX_DDC_WAIT_MS));

export const getDeviceDataCollection = (now: number = Date.now()): DeviceDataCollection | undefined => {
  if (setupStartedAt === null) {
    return undefined;
  }
  if (setupFailed) {
    return { status: 'error', waitMs: toWaitMs(now - setupStartedAt) };
  }
  if (setupCompletedAt !== null) {
    return { status: 'completed', waitMs: toWaitMs(setupCompletedAt - setupStartedAt) };
  }
  return { status: 'timeout', waitMs: toWaitMs(now - setupStartedAt) };
};
